import { type ReactNode } from "react";

import { useResponsive } from "@design-lab/shared/hooks/useResponsive";

export type TabNavigationItem<T extends string = string> = {
  id: T;
  label: string;
  icon?: ReactNode;
  testId: string;
};

export type TabNavigationProps<T extends string = string> = {
  tabs: TabNavigationItem<T>[];
  activeTab: T;
  onTabChange: (tabId: T) => void;
  className?: string;
};

type TabNavigationTabProps<T extends string = string> = {
  tab: TabNavigationItem<T>;
  isActive: boolean;
  onClick: () => void;
};

function TabNavigationTab<T extends string = string>({
  tab,
  isActive,
  onClick
}: TabNavigationTabProps<T>) {
  const { isDesktop } = useResponsive();
  const baseClasses = `flex flex-1 items-center justify-center gap-2 rounded-lg px-2 py-1 font-semibold transition-all border ${isDesktop ? "text-sm" : "text-xs"}`;
  const activeClasses = "bg-blue-600 text-white shadow-md border-blue-200";
  const inactiveClasses = "text-gray-700 hover:text-blue-600 border-transparent";

  const tabClasses = `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;

  return (
    <button className={tabClasses} onClick={onClick} data-testid={tab.testId} type="button">
      {tab.icon && <div>{tab.icon}</div>}
      <span>{tab.label}</span>
    </button>
  );
}

export function TabNavigation<T extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  className = ""
}: TabNavigationProps<T>) {
  return (
    <div
      className={`flex h-11 w-full gap-1 rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm ${className}`}
    >
      {tabs.map(tab => (
        <TabNavigationTab
          key={tab.id}
          tab={tab}
          isActive={activeTab === tab.id}
          onClick={() => onTabChange(tab.id)}
        />
      ))}
    </div>
  );
}
